// src/components/Multiplayer/Tournament/Tournament.jsx
// Sit & Go tournament list + registration.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useWebSocket } from '../../../contexts/WebSocketContext';
import { tournamentService, calculateRentalFee } from '../../../services/tournamentService';
import { useWallet } from '../../../contexts/WalletContext';
import currencyService from '../../../services/CurrencyService';
import './Tournament.css';

const CURRENCY_OPTIONS = [
  { code: 'USD', label: 'USD — US Dollar' },
  { code: 'INR', label: 'INR — Indian Rupee' },
  { code: 'LRD', label: 'LRD — Liberian Dollar' },
  { code: 'CFA', label: 'CFA — Local currency' },
];

const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (const m of [0, 30]) {
    const hh = String(h).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    const ampm = h < 12 ? 'AM' : 'PM';
    TIME_OPTIONS.push({ value: `${hh}:${mm}`, label: `${hour12}:${mm} ${ampm}` });
  }
}

const Tournament = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { walletBalances, refreshWallet } = useWallet();
  const { socket, isConnected, emitWithPromise } = useWebSocket();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [registeringId, setRegisteringId] = useState(null);
  const [myRegistrations, setMyRegistrations] = useState({});
  const [registeringFor, setRegisteringFor] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [now, setNow] = useState(Date.now());
  const [activeTab, setActiveTab] = useState('public');
  const [notice, setNotice] = useState('');
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinBusy, setJoinBusy] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({ maxPlayers: '24', advancePerRound: '3', prize1: '', prize2: '', prize3: '', message: '', currency: 'USD' });
  const [requestBusy, setRequestBusy] = useState(false);
  const [extraPrizes, setExtraPrizes] = useState([]);
  const [scheduleInput, setScheduleInput] = useState({});
  const [calendarMonth, setCalendarMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [openCalendar, setOpenCalendar] = useState(null);
  const [manageModal, setManageModal] = useState(null);
  const [joinRealName, setJoinRealName] = useState('');
  const [startingId, setStartingId] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [managing, setManaging] = useState({});
  const [manageLoading, setManageLoading] = useState(false);

  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  const goToGame = useCallback((gameId) => {
    if (gameId) navigate(`/game/${gameId}`);
  }, [navigate]);

  const loadTournaments = useCallback(async () => {
    try {
      setLoading(true);
      const list = await tournamentService.listMyTournaments();
      setTournaments(list || []);
    } catch (e) {
      setError(e.message || 'Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTournaments();
    tournamentService.listMyTournamentRequests()
      .then((list) => setMyRequests(list || []))
      .catch((e) => console.error('Failed to load requests:', e.message));
  }, [loadTournaments]);

  // Listen for the game to start so we can redirect to the table.
  useEffect(() => {
    if (!socket) return;
    const onStarted = (data) => { if (data && data.gameId) goToGame(data.gameId); };
    const onSeating = (data) => { if (data && data.gameId) goToGame(data.gameId); };
    socket.on('tournament-started', onStarted);
    socket.on('seating-phase-started', onSeating);
    return () => {
      socket.off('tournament-started', onStarted);
      socket.off('seating-phase-started', onSeating);
    };
  }, [socket, goToGame]);

  const balanceFor = (code) => {
    const b = walletBalances || {};
    if (code === 'USD') return b.USD || 0;
    if (code === 'INR') return b.INR || 0;
    if (code === 'LRD') return b.LRD != null ? b.LRD : (b.LOCAL || 0);
    return b.CFA != null ? b.CFA : (b.LOCAL || 0);
  };

  // Whether the logged-in player can rejoin their live tournament game.
  const getGameJoinState = (t) => {
    const JOIN_WINDOW_MS = 10 * 60 * 1000;
    if (!t || !t.registered || t.myGameId == null) return { kind: 'none' };
    if (!t.myGameStartedAt) return { kind: 'join', gameId: t.myGameId };
    const started = new Date(t.myGameStartedAt).getTime();
    if (isNaN(started)) return { kind: 'join', gameId: t.myGameId };
    return now <= started + JOIN_WINDOW_MS
      ? { kind: 'join', gameId: t.myGameId }
      : { kind: 'late' };
  };

  const handleRegisterClick = (tournament) => {
    if (!currentUser) { setError('Please log in to register for tournaments.'); return; }
    const fee = parseFloat(tournament.entryFee || 0);
    if (fee <= 0) {
      handleRegister(tournament, null);
    } else {
      setRegisteringFor(tournament);
      setSelectedCurrency('USD');
    }
  };

  const handleRegister = async (tournament, currency = null, realName = null) => {
    if (!currentUser) { setError('Please log in to register for tournaments.'); return; }
    if (!isConnected || !emitWithPromise) { setError('Connection unavailable. Please try again.'); return; }
    setRegisteringId(tournament.id);
    setError('');
    try {
      const res = await emitWithPromise('join-tournament', { tournamentId: tournament.id, currency, realName }, 15000);
      if (res && res.success) {
        setMyRegistrations((prev) => ({ ...prev, [tournament.id]: true }));
        // If the tournament became full, the server auto-launches and emits
        // tournament-started / seating-phase-started, which redirects us.
      } else {
        setError((res && res.error) || 'Registration failed');
      }
    } catch (e) {
      setError(e.message || 'Registration failed');
    } finally {
      setRegisteringId(null);
    }
  };

  const confirmRegister = async () => {
    const t = registeringFor;
    if (!t) return;
    const currency = selectedCurrency || null;
    setRegisteringFor(null);
    await handleRegister(t, currency);
  };

  const handleJoinByCode = async () => {
    const code = (joinCode || '').trim().toUpperCase();
    if (!code) { setError('Please enter a tournament code'); return; }
    setJoinBusy(true); setError('');
    try {
      const res = await tournamentService.joinByCode(code, (joinRealName || '').trim() || null);
      if (res && res.success) {
        setNotice('Joined successfully!');
        setShowJoinModal(false);
        setJoinCode('');
        setJoinRealName('');
        await loadTournaments();
      } else {
        setError((res && res.error) || 'Could not join tournament');
      }
    } catch (e) {
      setError(e.message || 'Could not join tournament');
    } finally {
      setJoinBusy(false);
    }
  };

  const requestFee = calculateRentalFee(parseInt(requestForm.maxPlayers, 10) || 0);
  const requestCurrency = requestForm.currency || 'USD';
  const requestBalance = requestCurrency === 'CFA' ? (walletBalances.LOCAL || 0) : (walletBalances[requestCurrency] || 0);
  const requestFeeConverted = requestCurrency === 'USD' ? (requestFee || 0) : currencyService.convertFromUSD(requestFee || 0, requestCurrency);
  const requestInsufficient = requestFee != null && requestFee > 0 && requestBalance < requestFeeConverted;

  const addExtraPrize = () => setExtraPrizes((prev) => [...prev, { id: Date.now(), name: '', amount: '' }]);
  const updateExtraPrize = (id, field, value) => setExtraPrizes((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
  const removeExtraPrize = (id) => setExtraPrizes((prev) => prev.filter((p) => p.id !== id));

  const handleSubmitRequest = async () => {
    const maxPlayers = parseInt(requestForm.maxPlayers, 10) || 0;
    if (maxPlayers < 24) { setError('Minimum 24 players required for a private tournament'); return; }
    const advancePerRound = (requestForm.advancePerRound || '').split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n) && n > 0);
    const prizeStructure = [];
    if (requestForm.prize1) prizeStructure.push({ rank: 1, name: '1st prize', amount: parseFloat(requestForm.prize1) || 0 });
    if (requestForm.prize2) prizeStructure.push({ rank: 2, name: '2nd prize', amount: parseFloat(requestForm.prize2) || 0 });
    if (requestForm.prize3) prizeStructure.push({ rank: 3, name: '3rd prize', amount: parseFloat(requestForm.prize3) || 0 });
    extraPrizes.forEach((p, i) => {
      const amount = parseFloat(p.amount) || 0;
      if (amount > 0) {
        prizeStructure.push({ rank: 4 + i, name: (p.name || '').trim() || `Prize ${4 + i}`, amount });
      }
    });
    setRequestBusy(true); setError('');
    try {
      const res = await tournamentService.submitTournamentRequest({
        maxPlayers,
        advancePerRound,
        prizeStructure: prizeStructure.length ? prizeStructure : null,
        message: (requestForm.message || '').trim() || null,
        currency: requestCurrency,
      });
      if (res && res.success) {
        setNotice(`Request sent! $${calculateRentalFee(maxPlayers).toFixed(2)} has been deducted from your wallet and held. It will be refunded if your request is declined.`);
        setShowRequestModal(false);
        setRequestForm({ maxPlayers: '24', advancePerRound: '3', prize1: '', prize2: '', prize3: '', message: '', currency: 'USD' });
        setExtraPrizes([]);
        refreshWallet();
        tournamentService.listMyTournamentRequests().then((list) => setMyRequests(list || [])).catch(() => {});
      } else {
        setError((res && res.error) || 'Could not send request');
      }
    } catch (e) {
      setError(e.message || 'Could not send request');
    } finally {
      setRequestBusy(false);
    }
  };

  const handleSetSchedule = async (t) => {
    const val = scheduleInput[t.id];
    if (!val) { setError('Pick a start date and time first'); return; }
    setError('');
    try {
      await tournamentService.updateTournamentSchedule(t.id, { tournamentStart: new Date(val).toISOString() });
      setNotice('Tournament date/time saved. Players can now see when it starts.');
      await loadTournaments();
    } catch (e) { setError(e.message || 'Could not save schedule'); }
  };

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const maxSelectableDate = new Date(todayStart);
  maxSelectableDate.setDate(todayStart.getDate() + 30);

  const calendarCells = (() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDayOfWeek = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    return cells;
  })();

  const formatCalendarDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const changeCalendarMonth = (delta) => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));

  const setScheduleDate = (tournamentId, dateStr) => {
    setScheduleInput((prev) => {
      const current = prev[tournamentId] || '';
      const time = current.slice(11, 16) || '09:00';
      return { ...prev, [tournamentId]: dateStr ? `${dateStr}T${time}` : '' };
    });
  };

  const setScheduleTime = (tournamentId, timeStr) => {
    setScheduleInput((prev) => {
      const current = prev[tournamentId] || '';
      const date = current.slice(0, 10);
      return { ...prev, [tournamentId]: date ? `${date}T${timeStr}` : '' };
    });
  };

  const handleStartTournament = async (t) => {
    if (!isConnected || !emitWithPromise) { setError('Connection unavailable. Please try again.'); return; }
    setStartingId(t.id); setError('');
    try {
      const res = await emitWithPromise('start-tournament', { tournamentId: t.id }, 15000);
      if (res && res.success) {
        setNotice('Tournament started!');
      } else {
        setError((res && res.error) || 'Could not start tournament');
      }
    } catch (e) { setError(e.message || 'Could not start tournament'); }
    finally { setStartingId(null); }
  };

  const openMonitor = (t) => {
    window.open(`/tournament/${t.id}/monitor`, '_blank', 'noopener,noreferrer');
  };

  const canStartTournament = (t) => {
    const registered = t.registeredCount != null ? t.registeredCount : (t.currentPlayers || 0);
    const approved = t.approvedCount != null ? t.approvedCount : (t.currentPlayers || 0);
    const target = Math.min(registered, t.maxPlayers || 0);
    return approved === target && approved >= (t.minPlayers || 2);
  };

  const handleManagePlayers = async (t) => {
    setManageLoading(true); setError('');
    try {
      const detail = await tournamentService.getTournament(t.id);
      setManageModal(detail);
    } catch (e) { setError(e.message || 'Could not load players'); }
    finally { setManageLoading(false); }
  };

  const handleTogglePlayer = async (t, player, approved) => {
    try {
      await tournamentService.setPlayerApproval(t.id, player.userId, approved);
      const detail = await tournamentService.getTournament(t.id);
      setManageModal(detail);
      await loadTournaments();
    } catch (e) { setError(e.message || 'Could not update player'); }
  };

  const handleOrganizerJoin = (t) => {
    const realName = window.prompt('Enter your real name (shown to players and the organizer):');
    if (realName === null) return;
    handleRegister(t, null, realName.trim() || null);
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setNotice(`Code ${code} copied to clipboard — share it with your players.`);
    } catch (e) {
      setNotice(`Your tournament code: ${code}`);
    }
  };

  const formatCountdown = (target) => {
    if (!target) return null;
    const diff = new Date(target).getTime() - now;
    if (diff <= 0) return null;
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return `${d}d ${h}h ${m}m ${s}s`;
  };

  const formatStartsIn = (target) => {
    if (!target) return null;
    const diff = new Date(target).getTime() - now;
    if (diff <= 0) return null;
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    const parts = [];
    if (d > 0) parts.push(`${d}d`);
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    parts.push(`${s}s`);
    return parts.join(' ');
  };

  const getRegistrationStatus = (t) => {
    if (!t.registrationStart && !t.registrationEnd) return null;
    const rs = t.registrationStart ? new Date(t.registrationStart).getTime() : null;
    const re = t.registrationEnd ? new Date(t.registrationEnd).getTime() : null;
    if (rs && now < rs) return { label: 'Registration opens in', countdown: formatCountdown(rs) };
    if (re && now < re) return { label: 'Registration closes in', countdown: formatCountdown(re) };
    if (re && now >= re) return { label: 'Registration closed', countdown: null };
    return { label: 'Registration open', countdown: null };
  };

  const formatEntry = (t) => {
    const fee = parseFloat(t.entryFee || 0);
    const c = (t.currency || 'USD').toUpperCase();
    if (fee === 0) return 'FREE';
    const sym = { USD: '$', INR: '₹', LRD: 'L$', XOF: 'CFA', CFA: 'CFA' }[c] || '';
    return `${sym}${fee}`;
  };

  if (!currentUser) {
    return (
      <div className="t-container">
        <button className="t-back" onClick={() => navigate(-1)}>← Back</button>
        <div className="t-auth-required">
          <h2>🏆 Tournaments</h2>
          <p>Please log in to view and join tournaments.</p>
        </div>
      </div>
    );
  }

  // Scheduled tournament categories (default tournaments shown in sections)
  const CATEGORIES = [
    { key: 'daily', label: '📅 Daily', icon: '📅' },
    { key: 'friday_special', label: '🎉 Friday Special', icon: '🎉' },
    { key: 'saturday_special', label: '🎊 Saturday Special', icon: '🎊' },
    { key: 'sunday_special', label: '👑 Sunday Special', icon: '👑' },
    { key: 'custom', label: '🏆 Other', icon: '🏆' },
  ];

  const activeTournaments = tournaments.filter((t) => t.isActive !== false);
  const publicTournaments = activeTournaments.filter((t) => t.isPrivate !== true);
  const myPrivateTournaments = activeTournaments.filter((t) => t.isPrivate === true);

  return (
    <div className="t-container">
      <button className="t-back" onClick={() => navigate(-1)}>← Back</button>
      <div className="t-header">
        <h1>🏆 Tournaments</h1>
        <p>Compete in rummy tournaments and climb the leaderboard!</p>
      </div>

      {error && <div className="t-error">{error}</div>}
      {notice && <div className="t-notice">{notice}</div>}

      <div className="t-tabs">
        <button className={`t-tab ${activeTab === 'public' ? 'active' : ''}`} onClick={() => setActiveTab('public')}>Public Tournaments</button>
        <button className={`t-tab ${activeTab === 'custom' ? 'active' : ''}`} onClick={() => setActiveTab('custom')}>Private Tournaments</button>
      </div>

      {loading ? (
        <div className="t-loading">Loading tournaments...</div>
      ) : activeTab === 'custom' ? (
        <div className="t-private-section">
          <div className="t-actions-bar">
            <button className="t-action-btn" onClick={() => setShowJoinModal(true)}>🎟️ Join with code</button>
            <button className="t-action-btn" onClick={() => setShowRequestModal(true)}>📩 Request a private tournament</button>
          </div>
          {myPrivateTournaments.length === 0 ? (
            <div className="t-empty">No custom tournaments yet. Use "Join with code" to join one, or request a private tournament from your admin.</div>
          ) : (
            <div className="t-grid">
              {myPrivateTournaments.map((t) => (
                <div className="t-card t-private-card" key={t.id}>
                  <div className="t-card-header">
                    <h3 className="t-title">{t.name}</h3>
                    <span className={`t-status ${t.status}`}>{t.status}</span>
                  </div>
                  <p className="t-description">{t.description || 'Private tournament'}</p>
                  {t.isOrganizer ? (
                    <div className="t-code-box">
                      <span className="t-code-label">Share this code with your players</span>
                      <span className="t-code-value">{t.tournamentCode}</span>
                      <button className="t-copy-btn" onClick={() => copyCode(t.tournamentCode)}>📋 Copy code</button>
                      <span className="t-code-expiry">
                        {formatCountdown(t.codeExpiresAt) ? `Expires in ${formatCountdown(t.codeExpiresAt)}` : 'Code expired'}
                      </span>
                    </div>
                  ) : t.registered && t.myApproved === false ? (
                    <div style={{ padding: '10px', borderRadius: '6px', background: '#2a1f08', border: '1px solid #b45309', color: '#fde68a', fontSize: '14px' }}>
                      🙏 You are not eligible for this tournament yet — please contact the organizer to add you manually.
                    </div>
                  ) : t.myEliminated ? (
                    <div style={{ padding: '10px', borderRadius: '6px', background: '#0d2a12', border: '1px solid #2e7d32', color: '#c8e6c9', fontSize: '14px' }}>
                      🌟 Well played! Better luck next time — keep practicing, you'll get there.
                    </div>
                  ) : (
                    <div className="t-joined-badge">✅ You're in this tournament</div>
                  )}
                  {t.isOrganizer && (
                    <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {t.status === 'registration' && (
                        <>
                          <div style={{ fontSize: '14px', marginBottom: '2px' }}>
                            <strong>Players:</strong> {t.approvedCount ?? t.currentPlayers}/{t.maxPlayers} selected ({t.registeredCount ?? t.currentPlayers} registered)
                          </div>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>
                            Start date & time
                            <div className="t-date-picker">
                              <button type="button" className="t-date-picker-field" onClick={() => setOpenCalendar(openCalendar === t.id ? null : t.id)}>
                                <span className={(scheduleInput[t.id] || '').slice(0, 10) ? '' : 'placeholder'}>
                                  {(scheduleInput[t.id] || '').slice(0, 10) ? formatDisplayDate((scheduleInput[t.id] || '').slice(0, 10)) : 'Select date'}
                                </span>
                                <span className="t-date-picker-arrow">▾</span>
                              </button>
                              {openCalendar === t.id && (
                                <div className="t-calendar">
                                  <div className="t-calendar-header">
                                    <button type="button" className="t-calendar-nav" onClick={() => changeCalendarMonth(-1)}>‹</button>
                                    <span className="t-calendar-title">{calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                                    <button type="button" className="t-calendar-nav" onClick={() => changeCalendarMonth(1)}>›</button>
                                  </div>
                                  <div className="t-calendar-weekdays">
                                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => <span key={d}>{d}</span>)}
                                  </div>
                                  <div className="t-calendar-grid">
                                    {calendarCells.map((cell, idx) => {
                                      if (!cell) return <span key={`e${idx}`} className="t-calendar-empty" />;
                                      const dayStr = formatCalendarDate(cell);
                                      const isActive = cell >= todayStart && cell <= maxSelectableDate;
                                      const isSelected = (scheduleInput[t.id] || '').slice(0, 10) === dayStr;
                                      return (
                                        <button
                                          key={idx}
                                          type="button"
                                          className={`t-calendar-day${isActive ? '' : ' disabled'}${isSelected ? ' selected' : ''}`}
                                          disabled={!isActive}
                                          onClick={() => { setScheduleDate(t.id, dayStr); setOpenCalendar(null); }}
                                        >
                                          {cell.getDate()}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>
                              Time
                              <select className="t-schedule-select" value={(scheduleInput[t.id] || '').slice(11, 16)} onChange={(e) => setScheduleTime(t.id, e.target.value)}>
                                <option value="">Select time</option>
                                {TIME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            </label>
                          </label>
                          <button className="t-copy-btn" onClick={() => handleSetSchedule(t)}>💾 Save date/time</button>
                        </>
                      )}
                    </div>
                  )}
                  {t.tournamentStart && (
                    <div className="t-detail"><span className="t-detail-label">Starts</span><span className="t-detail-value">{new Date(t.tournamentStart).toLocaleString()}</span></div>
                  )}
                  {t.tournamentStart && formatStartsIn(t.tournamentStart) && (
                    <div className="t-countdown">⏳ Starts in <strong>{formatStartsIn(t.tournamentStart)}</strong></div>
                  )}
                  <div className="t-details">
                    <div className="t-detail"><span className="t-detail-label">Players</span><span className="t-detail-value">{t.currentPlayers}/{t.maxPlayers}</span></div>
                    <div className="t-detail"><span className="t-detail-label">Points</span><span className="t-detail-value">{t.settings?.pointsTarget || 201}</span></div>
                  </div>
                  {t.displayPrize > 0 ? (
                    <div className="t-display-prize">Prize pool: ${parseFloat(t.displayPrize).toFixed(2)}</div>
                  ) : (
                    <div className="t-display-prize t-display-prize-empty">Prize settled by organizer</div>
                  )}
                  <div className="t-disclaimer">*We are not responsible for this game. Discuss the prize directly with the organizer — he settles it manually.</div>
                  {t.isOrganizer && (
                    <div className="t-organizer-actions">
                      {t.status === 'registration' && (
                        <>
                          <button className="t-copy-btn" onClick={() => handleManagePlayers(t)}>👥 Manage players</button>
                          <button className="t-register-btn" onClick={() => handleStartTournament(t)} disabled={startingId === t.id || !canStartTournament(t)}>
                            {startingId === t.id ? 'Starting...' : t.tournamentStart && formatStartsIn(t.tournamentStart) ? `▶️ Start (${formatStartsIn(t.tournamentStart)})` : '▶️ Start tournament'}
                          </button>
                        </>
                      )}
                      {t.status === 'active' && (
                        <button className="t-monitor-btn" onClick={() => openMonitor(t)}>🖥️ Monitor tables</button>
                      )}
                      {!t.registered && t.status === 'registration' && (
                        <button className="t-register-btn" onClick={() => handleOrganizerJoin(t)}>Join your tournament</button>
                      )}
                    </div>
                  )}
                  {t.status === 'completed' && (
                    <button className="t-results-btn" onClick={() => navigate(`/tournament/${t.id}/results`)}>View Results</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : publicTournaments.length === 0 ? (
        <div className="t-empty">No tournaments available right now. Check back soon!</div>
      ) : (
        CATEGORIES.map((cat) => {
          const items = publicTournaments.filter((t) => (t.category || 'custom') === cat.key);
          if (items.length === 0) return null;
          return (
            <div className="t-category" key={cat.key}>
              <div className="t-category-header">
                <span className="t-category-icon">{cat.icon}</span>
                <h2 className="t-category-title">{cat.label}</h2>
                <span className="t-category-count">{items.length}</span>
              </div>
              <div className="t-grid">
                {items.map((t) => (
                  <div className="t-card" key={t.id}>
                    <div className="t-card-header">
                      <div className="t-card-title-row">
                        <h3 className="t-title">{t.name}</h3>
                        {t.isDefault && <span className="t-default-badge">DEFAULT</span>}
                      </div>
                      <span className={`t-status ${t.status}`}>{t.status}</span>
                    </div>
                    <p className="t-description">{t.description || 'Pool rummy tournament'}</p>
                    {getRegistrationStatus(t) && (
                      <div className="t-schedule">{getRegistrationStatus(t).label}{getRegistrationStatus(t).countdown ? `: ${getRegistrationStatus(t).countdown}` : ''}</div>
                    )}
                    <div className="t-prize-note">Est. prize: {formatEntry(t)} x {t.maxPlayers} players · *Prizes subject to change based on registered players.</div>
                    <div className="t-prize-row">
                      <div className="t-prize-label">Prize Pool</div>
                      <div className="t-prize-amount">{formatEntry(t)} x{t.maxPlayers} ≈ ${parseFloat(t.prizePool || 0).toFixed(2)}</div>
                    </div>
                    <div className="t-details">
                      <div className="t-detail">
                        <span className="t-detail-label">Entry</span>
                        <span className="t-detail-value">{formatEntry(t)}</span>
                      </div>
                      <div className="t-detail">
                        <span className="t-detail-label">Players</span>
                        <span className="t-detail-value">{t.currentPlayers}/{t.maxPlayers}</span>
                      </div>
                      <div className="t-detail">
                        <span className="t-detail-label">Points</span>
                        <span className="t-detail-value">{t.settings?.pointsTarget || 201}</span>
                      </div>
                    </div>
                    <div className="t-progress-bar">
                      <div
                        className="t-progress-fill"
                        style={{ width: `${Math.min(100, (t.currentPlayers / t.maxPlayers) * 100)}%` }}
                      />
                    </div>
                    {(() => {
                      const join = getGameJoinState(t);
                      if (join.kind === 'join') {
                        return (
                          <button className="t-register-btn t-go-btn" onClick={() => goToGame(join.gameId)}>
                            ▶️ Go to game
                          </button>
                        );
                      }
                      if (join.kind === 'late') {
                        return (
                          <div className="t-late-msg">⏰ Game already in progress — cannot join. Better luck next time!</div>
                        );
                      }
                      return (
                        <button
                          className="t-register-btn"
                          onClick={() => handleRegisterClick(t)}
                          disabled={registeringId === t.id || myRegistrations[t.id] || t.status !== 'registration'}
                        >
                          {registeringId === t.id ? (
                            <><span className="t-spinner" /> Registering...</>
                          ) : myRegistrations[t.id] ? (
                            'Registered ✓'
                          ) : t.status !== 'registration' ? (
                            'Closed'
                          ) : (
                            `Register (${formatEntry(t)})`
                          )}
                        </button>
                      );
                    })()}
                    {t.status === 'completed' && (
                      <button
                        className="t-results-btn"
                        onClick={() => navigate(`/tournament/${t.id}/results`)}
                      >
                        View Results
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}

      <div className="t-info-section">
        <h3>❓ How Tournaments Work</h3>
        <div className="t-steps">
          <div className="t-step"><span className="t-step-num">1</span><span>Register before the table fills up</span></div>
          <div className="t-step"><span className="t-step-num">2</span><span>Entry fee is deducted from your wallet</span></div>
          <div className="t-step"><span className="t-step-num">3</span><span>Play a single table of pool rummy</span></div>
          <div className="t-step"><span className="t-step-num">4</span><span>Top players share the prize pool</span></div>
        </div>
      </div>

      {registeringFor && (
        <div className="t-modal-overlay" onClick={() => setRegisteringFor(null)}>
          <div className="t-modal" onClick={(e) => e.stopPropagation()}>
            <h3>💱 Choose wallet</h3>
            <p className="t-modal-hint">Entry fee: ${parseFloat(registeringFor.entryFee || 0).toFixed(2)} — pay from any wallet you have.</p>
            <div className="t-currency-list">
              {CURRENCY_OPTIONS.map((c) => {
                const bal = balanceFor(c.code);
                return (
                  <button
                    key={c.code}
                    className={`t-currency-opt ${selectedCurrency === c.code ? 'active' : ''}`}
                    onClick={() => setSelectedCurrency(c.code)}
                  >
                    <span className="t-currency-code">{c.code}</span>
                    <span className="t-currency-bal">Balance: {currencyService.format(bal, c.code)}</span>
                  </button>
                );
              })}
            </div>
            <button className="t-modal-btn" onClick={confirmRegister} disabled={registeringId === registeringFor.id}>
              {registeringId === registeringFor.id ? 'Registering...' : 'Confirm & Pay'}
            </button>
            <button className="t-modal-cancel" onClick={() => setRegisteringFor(null)}>Cancel</button>
          </div>
        </div>
      )}

      {showJoinModal && (
        <div className="t-modal-overlay" onClick={() => setShowJoinModal(false)}>
          <div className="t-modal" onClick={(e) => e.stopPropagation()}>
            <h3>🎟️ Join with code</h3>
            <input className="t-modal-input" value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="Enter tournament code" />
            <input className="t-modal-input" value={joinRealName} onChange={(e) => setJoinRealName(e.target.value)} placeholder="Your real name (optional)" />
            <button className="t-modal-btn" onClick={handleJoinByCode} disabled={joinBusy}>{joinBusy ? 'Joining...' : 'Join'}</button>
            <button className="t-modal-cancel" onClick={() => setShowJoinModal(false)}>Cancel</button>
          </div>
        </div>
      )}

      {manageModal && (
        <div className="t-modal-overlay" onClick={() => setManageModal(null)}>
          <div className="t-modal" onClick={(e) => e.stopPropagation()}>
            <h3>👥 Manage players — {manageModal.name}</h3>
            <div style={{ fontSize: '13px', opacity: 0.8 }}>Approve or remove players before starting.</div>
            {(manageModal.players || []).length === 0 ? (
              <div style={{ fontSize: '13px', opacity: 0.7 }}>No players registered yet.</div>
            ) : (
              <div style={{ maxHeight: '300px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {(manageModal.players || []).map((p) => (
                  <label key={p.userId} className="t-manage-player">
                    <input type="checkbox" checked={p.approved !== false} onChange={(e) => handleTogglePlayer(manageModal, p, e.target.checked)} />
                    <span className="t-manage-username">{p.username}</span>
                    <span className="t-manage-realname">{p.realName || '—'}</span>
                  </label>
                ))}
              </div>
            )}
            <button className="t-modal-cancel" onClick={() => setManageModal(null)}>Close</button>
          </div>
        </div>
      )}

      {showRequestModal && (
        <div className="t-modal-overlay" onClick={() => setShowRequestModal(false)}>
          <div className="t-modal" onClick={(e) => e.stopPropagation()}>
            <h3>📩 Request a private tournament</h3>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', marginBottom: '10px' }}>
              Total players (minimum 24)
              <input type="number" min="24" className="t-modal-input" value={requestForm.maxPlayers} onChange={(e) => setRequestForm((p) => ({ ...p, maxPlayers: e.target.value }))} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', marginBottom: '10px' }}>
              Currency
              <select className="t-modal-input" value={requestCurrency} onChange={(e) => setRequestForm((p) => ({ ...p, currency: e.target.value }))}>
                {CURRENCY_OPTIONS.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
            </label>
            <div style={{ background: '#111', padding: '10px', borderRadius: '6px', marginBottom: '10px', fontSize: '14px' }}>
              <div>Balance: <strong>{currencyService.format(requestBalance, requestCurrency)}</strong></div>
              <div>Rental fee: <strong>{requestFee == null ? '—' : currencyService.format(requestFeeConverted, requestCurrency)}{requestCurrency !== 'USD' && requestFee != null ? ` ($${requestFee.toFixed(2)} USD)` : ''}</strong></div>
              {requestInsufficient && (
                <div style={{ color: '#f44336', marginTop: '6px', fontWeight: '600' }}>
                  ⚠️ Insufficient funds — you need {currencyService.format(requestFeeConverted - requestBalance, requestCurrency)} more.
                </div>
              )}
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', marginBottom: '10px' }}>
              Players advancing per table, each round (comma separated, e.g. 3,2)
              <input type="text" className="t-modal-input" value={requestForm.advancePerRound} onChange={(e) => setRequestForm((p) => ({ ...p, advancePerRound: e.target.value }))} placeholder="3,2" />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', marginBottom: '10px' }}>
              First prize (USD, optional)
              <input type="number" min="0" className="t-modal-input" value={requestForm.prize1} onChange={(e) => setRequestForm((p) => ({ ...p, prize1: e.target.value }))} placeholder="100" />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', marginBottom: '10px' }}>
              Second prize (USD, optional)
              <input type="number" min="0" className="t-modal-input" value={requestForm.prize2} onChange={(e) => setRequestForm((p) => ({ ...p, prize2: e.target.value }))} placeholder="50" />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', marginBottom: '10px' }}>
              Third prize (USD, optional)
              <input type="number" min="0" className="t-modal-input" value={requestForm.prize3} onChange={(e) => setRequestForm((p) => ({ ...p, prize3: e.target.value }))} placeholder="25" />
            </label>
            {extraPrizes.map((p) => (
              <div key={p.id} className="t-extra-prize">
                <div className="t-extra-prize-head">
                  <span>Additional prize</span>
                  <button type="button" className="t-extra-prize-remove" onClick={() => removeExtraPrize(p.id)} aria-label="Remove prize">✕</button>
                </div>
                <input type="text" className="t-modal-input" placeholder="Name (e.g. 4th prize or 4th to 10th)" value={p.name} onChange={(e) => updateExtraPrize(p.id, 'name', e.target.value)} />
                <input type="number" min="0" className="t-modal-input" placeholder="Amount (USD)" value={p.amount} onChange={(e) => updateExtraPrize(p.id, 'amount', e.target.value)} />
              </div>
            ))}
            <button type="button" className="t-add-prize-btn" onClick={addExtraPrize}>+ Add another prize</button>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', marginBottom: '10px' }}>
              Notes (optional)
              <textarea rows={2} className="t-modal-input" value={requestForm.message} onChange={(e) => setRequestForm((p) => ({ ...p, message: e.target.value }))} placeholder="Any other details..." />
            </label>
            <button className="t-modal-btn" onClick={handleSubmitRequest} disabled={requestBusy}>{requestBusy ? 'Sending...' : 'Send request'}</button>
            <button className="t-modal-cancel" onClick={() => setShowRequestModal(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tournament;
